import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useT } from '../i18n';

interface ResizablePanelProps {
  side: 'left' | 'right';
  /** Klíč pro perzistenci šířky/sbalení do `localStorage` — uživatelská preference, ne obsah projektu (E11-US6). */
  storageKey: string;
  defaultWidth: number;
  minWidth?: number;
  maxWidth?: number;
  children: ReactNode;
}

function readStoredWidth(storageKey: string, fallback: number): number {
  const raw = window.localStorage.getItem(`${storageKey}.width`);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
}

function readStoredCollapsed(storageKey: string): boolean {
  return window.localStorage.getItem(`${storageKey}.collapsed`) === '1';
}

/**
 * Postranní panel s tažením za okraj (resize) a sbalením do minimálního
 * stavu (E11-US6). Vlastní implementace místo externí knihovny — jde jen
 * o jeden ovládaný rozměr s min/max clampem, netřeba stavět na míru nic
 * složitějšího (viz epic-11 kap. 8, kde je knihovna zmíněná jen jako opce).
 */
export function ResizablePanel({ side, storageKey, defaultWidth, minWidth = 200, maxWidth = 480, children }: ResizablePanelProps) {
  const t = useT();
  const [width, setWidth] = useState(() => readStoredWidth(storageKey, defaultWidth));
  const [collapsed, setCollapsed] = useState(() => readStoredCollapsed(storageKey));
  const dragState = useRef<{ startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    window.localStorage.setItem(`${storageKey}.width`, String(width));
  }, [storageKey, width]);

  useEffect(() => {
    window.localStorage.setItem(`${storageKey}.collapsed`, collapsed ? '1' : '0');
  }, [storageKey, collapsed]);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!dragState.current) return;
      const delta = event.clientX - dragState.current.startX;
      const signedDelta = side === 'left' ? delta : -delta;
      const next = Math.min(maxWidth, Math.max(minWidth, dragState.current.startWidth + signedDelta));
      setWidth(next);
    },
    [side, minWidth, maxWidth],
  );

  const stopDragging = useCallback(() => {
    dragState.current = null;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', stopDragging);
  }, [handlePointerMove]);

  const startDragging = (event: React.PointerEvent) => {
    dragState.current = { startX: event.clientX, startWidth: width };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopDragging);
  };

  useEffect(() => stopDragging, [stopDragging]);

  if (collapsed) {
    return (
      <div className={`workspace-panel workspace-panel--collapsed workspace-panel--${side}`}>
        <button type="button" className="workspace-panel__collapse-toggle" title={t('workspace.panel.expand')} onClick={() => setCollapsed(false)}>
          {side === 'left' ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
        </button>
      </div>
    );
  }

  // Sbalovací tlačítko žije ve vlastním úzkém pruhu MIMO scrollovatelný obsah
  // (ne absolutně přes něj) — jinak by uprostřed dlouhého seznamu překrývalo
  // text, ať by bylo umístěné kdekoliv svisle (viz oprava E11-US6).
  const toggle = (
    <div className="workspace-panel__toggle-strip">
      <button type="button" className="workspace-panel__collapse-toggle" title={t('workspace.panel.collapse')} onClick={() => setCollapsed(true)}>
        {side === 'left' ? <ChevronsLeft size={16} /> : <ChevronsRight size={16} />}
      </button>
    </div>
  );
  const content = <div className="workspace-panel__content">{children}</div>;

  return (
    <div className={`workspace-panel workspace-panel--${side}`} style={{ width }}>
      {side === 'right' && (
        <div className="workspace-panel__resize-handle workspace-panel__resize-handle--left" onPointerDown={startDragging} />
      )}
      {side === 'left' ? (
        <>
          {content}
          {toggle}
        </>
      ) : (
        <>
          {toggle}
          {content}
        </>
      )}
      {side === 'left' && (
        <div className="workspace-panel__resize-handle workspace-panel__resize-handle--right" onPointerDown={startDragging} />
      )}
    </div>
  );
}
